import React from 'react';

import {assert} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CollectionPageTemplateCollectionNode} from 'notebook/entry-templates/collections/TemplateCollectionTable';
import {
  TemplateCollectionQuery,
  TemplateCollectionQueryVariables,
} from 'notebook/template-collections/graphql/__generated/TemplateCollectionQuery';
import {TEMPLATE_COLLECTION_QUERY} from 'notebook/template-collections/graphql/TemplateCollectionsQueries';
import TemplateCollectionUtil from 'notebook/template-collections/TemplateCollectionUtil';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

import NewTemplateCollectionButton from './NewTemplateCollectionButton';

export interface TemplateCollectibleCreateButtonInternalProps extends Pick<
  ButtonProps,
  'text' | 'isDropdown' | 'disabled'
> {
  collection: CollectionPageTemplateCollectionNode;
  // TODO(BNCH-121653): We should be able to make compact required and remove text here
  compact?: boolean;
  // TODO(BNCH-121653) - Currently, the TemplateCollectibleCreateButton is used in both the
  // `TemplateCollectionsSidepanel` and in the `TemplateCollectionTable`. The way that we render it is
  // slightly different but the functionality is identical in both. The `legacyMode` flag here is intended to
  // provide signal for when we are in the `TemplateCollectionTable`. Once we remove
  // `ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE`, we can remove the `legacyMode` flag.
  legacyMode?: boolean;
}

function TemplateCollectibleCreateButtonInternal({
  collection,
  disabled,
  compact,
  legacyMode = true,
  text = 'Create',
  isDropdown = true,
}: TemplateCollectibleCreateButtonInternalProps): JSX.Element {
  const canCreateCollectibles = collection.permissions && collection.permissions.appendable;
  const templateTypes = TemplateCollectionUtil.getTemplateCollectionTypes(collection);
  const {
    buttonStyle,
    icon,
    displayText,
  }: {buttonStyle: ButtonStyle; icon?: BenchlingIconName; displayText?: React.ReactNode} = legacyMode
    ? {buttonStyle: ButtonStyle.PRIMARY, icon: undefined, displayText: text}
    : {
        buttonStyle: compact ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.SECONDARY,
        icon: 'compose' satisfies BenchlingIconName,
        displayText: undefined,
      };

  return (
    <Dropdown
      menu={
        <Menu width={MenuWidth.SMALL} dropLeft={!compact && !legacyMode}>
          {templateTypes
            .filter(({isCreatable}) => isCreatable(collection))
            .map(({name, value, createTemplateTypeClickHandler}) => (
              <Option
                key={value}
                onSelect={createTemplateTypeClickHandler}
                dataTestAttrs={{component: 'TemplateCollectionPage', element: 'option', key: name}}
              >
                {name}
              </Option>
            ))}
        </Menu>
      }
    >
      <Button
        isDropdown={isDropdown}
        buttonStyle={buttonStyle}
        icon={icon}
        text={displayText}
        size={ButtonSize.SMALL}
        disabled={disabled || !canCreateCollectibles}
        dataTestAttrs={{component: 'TemplateCollectionPage', element: 'button', key: 'create'}}
      />
    </Dropdown>
  );
}

interface TemplateCollectibleCreateButtonProps extends Omit<
  TemplateCollectibleCreateButtonInternalProps,
  'collection'
> {
  collectionId: string;
}

function TemplateCollectibleCreateButton({
  collectionId,
  compact,
  legacyMode,
  isDropdown = true,
  disabled = false,
}: TemplateCollectibleCreateButtonProps): JSX.Element {
  // TODO(BNCH-123598): Remove query once we can pass down the full Collection object
  return (
    <BenchlingQuery<TemplateCollectionQuery, TemplateCollectionQueryVariables>
      query={TEMPLATE_COLLECTION_QUERY}
      variables={{id: collectionId}}
      fetchPolicy="cache-and-network"
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data) => {
        assert(!isInaccessible(data.templateCollection), 'Expected result schema to be accessible.');
        const collection = data.templateCollection;
        return (
          <TemplateCollectibleCreateButtonInternal
            compact={compact}
            collection={collection}
            isDropdown={isDropdown}
            disabled={disabled}
            legacyMode={legacyMode}
          />
        );
      }}
    </BenchlingQuery>
  );
}

interface TemplateCollectionOrCollectibleCreateButtonProps extends Omit<
  TemplateCollectibleCreateButtonProps,
  'collectionId'
> {
  collectionId?: string;
}

export default function TemplateCollectionOrCollectibleCreateButton(
  props: TemplateCollectionOrCollectibleCreateButtonProps
): JSX.Element {
  const {collectionId} = props;
  if (collectionId !== undefined) {
    return <TemplateCollectibleCreateButton {...props} collectionId={collectionId} />;
  }
  return <NewTemplateCollectionButton {...props} compact={props.compact ?? true} />;
}
